import { useContext, useEffect, useState } from 'react';
import {
    Modal, ModalDialog, ModalClose,
    Box, Stack, Typography, Chip,
    Input, Select, Option, Button,
    IconButton,
} from '@mui/joy';

import CreditCardRoundedIcon   from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon     from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon  from '@mui/icons-material/PointOfSaleRounded';
import AddRoundedIcon          from '@mui/icons-material/AddRounded';
import ReceiptLongRoundedIcon  from '@mui/icons-material/ReceiptLongRounded';

import { AppContext } from '../../context';
import {
    APIGetEmployees,
    APICreateOrder,
    parseApiError,
} from '../../api';

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    color: 'var(--joy-palette-text-primary)',
    fontSize: 13,
    '&:hover, &:focus-within': {
        borderColor: 'var(--joy-palette-primary-500)',
        bgcolor: 'var(--joy-palette-background-level2)',
    },
};

const MONEY_TYPES = [
    { v: 'transfer', label: 'Перевод',  icon: <CreditCardRoundedIcon sx={{ fontSize: 14 }} />, c: '#818cf8' },
    { v: 'cash',     label: 'Наличные', icon: <PaymentsRoundedIcon sx={{ fontSize: 14 }} />,   c: '#22c55e' },
    { v: 'terminal', label: 'Терминал', icon: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} />, c: '#f59e0b' },
];

/**
 * Модалка добавления чека на рабочем аккаунте.
 *
 * Серверный create_order (декоратор @workstation_shift_required)
 * сам подставит location и administrator из активной смены —
 * нам нужно прислать только photographer_id, value, money_type.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onCreated: () => void  — вызвать после успешного создания (например, перезагрузить статистику)
 */
export default function WorkstationAddOrderModal({ open, onClose, onCreated }) {
    const { token, showToast } = useContext(AppContext);

    const [photographers, setPhotographers] = useState([]);
    const [loadingPh,     setLoadingPh]     = useState(false);

    const [phId,      setPhId]      = useState('');
    const [value,     setValue]     = useState('');
    const [moneyType, setMoneyType] = useState('transfer');
    const [submitting, setSubmitting] = useState(false);

    // ── Загрузка списка фотографов при открытии ──────────────
    useEffect(() => {
        if (!open || !token) return;
        let cancelled = false;
        (async () => {
            setLoadingPh(true);
            // Берём всех, кто может иметь чеки: фотограф + админ+фотограф
            const data = await APIGetEmployees(token, 'can_have_orders');
            if (cancelled) return;
            if (Array.isArray(data)) {
                setPhotographers(data.filter(e => !e.is_blocked && !e.is_fired));
            }
            setLoadingPh(false);
        })();
        return () => { cancelled = true; };
    }, [open, token]);

    // ── Сброс формы при закрытии ─────────────────────────────
    useEffect(() => {
        if (!open) {
            setPhId('');
            setValue('');
            setMoneyType('transfer');
            setSubmitting(false);
        }
    }, [open]);

    // ── Создание чека ────────────────────────────────────────
    const handleCreate = async () => {
        if (!phId) {
            showToast('Выберите фотографа', 'warning');
            return;
        }
        const v = parseFloat(value);
        if (!v || v <= 0) {
            showToast('Укажите сумму больше 0', 'warning');
            return;
        }

        setSubmitting(true);
        // location_id и administrator_id для workstation проставит сервер
        // из активной AdminShift
        const res = await APICreateOrder(token, {
            photographer_id: parseInt(phId),
            value:           v,
            money_type:      moneyType,
        });
        setSubmitting(false);

        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось создать чек'), 'danger');
            return;
        }

        showToast(`Чек добавлен — ${fmtMoney(v)} ₽`);
        if (onCreated) onCreated();
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={() => !submitting && onClose()}
            slotProps={{
                backdrop: {
                    sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' },
                },
            }}
            sx={{ touchAction: 'none', overscrollBehavior: 'contain' }}
        >
            <ModalDialog
                sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                    maxWidth: 460, width: '100%',
                    p: 3,
                    maxHeight: { xs: '100dvh', sm: '90vh' },
                    height:    { xs: '100dvh', sm: 'auto' },
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <ReceiptLongRoundedIcon sx={{ fontSize: 20, color: 'var(--joy-palette-primary-400)' }} />
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', letterSpacing: '-0.01em' }}>
                            Добавить чек
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                        Точка и админ смены подставятся автоматически
                    </Typography>
                </Box>

                <Stack spacing={2}>
                    {/* ── Фотограф ─────────────────────────────── */}
                    <Box>
                        <Typography sx={{
                            fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: 'var(--joy-palette-text-secondary)',
                            mb: 0.5,
                        }}>
                            Фотограф *
                        </Typography>
                        <Select
                            value={phId}
                            onChange={(_, v) => setPhId(v ?? '')}
                            placeholder={loadingPh ? 'Загрузка…' : 'Выберите фотографа'}
                            disabled={loadingPh || submitting}
                            size="sm"
                            sx={inputSx}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': { bgcolor: 'rgba(162,38,226,0.12)' },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            {photographers.map(p => (
                                <Option key={p.id} value={p.id}>
                                    {p.full_name || `${p.first_name} ${p.last_name}`.trim() || p.email}
                                </Option>
                            ))}
                        </Select>
                    </Box>

                    {/* ── Сумма ─────────────────────────────────── */}
                    <Box>
                        <Typography sx={{
                            fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: 'var(--joy-palette-text-secondary)',
                            mb: 0.5,
                        }}>
                            Сумма *
                        </Typography>
                        <Input
                            type="number"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder="0"
                            size="sm"
                            disabled={submitting}
                            endDecorator={
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>₽</Typography>
                            }
                            sx={inputSx}
                        />
                    </Box>

                    {/* ── Тип оплаты ───────────────────────────── */}
                    <Box>
                        <Typography sx={{
                            fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: 'var(--joy-palette-text-secondary)',
                            mb: 0.5,
                        }}>
                            Тип оплаты
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.8 }}>
                            {MONEY_TYPES.map(t => {
                                const active = moneyType === t.v;
                                return (
                                    <Box
                                        key={t.v}
                                        onClick={() => !submitting && setMoneyType(t.v)}
                                        sx={{
                                            py: 1.1,
                                            borderRadius: 'var(--joy-radius-md)',
                                            border: '1.5px solid',
                                            borderColor: active ? t.c : 'var(--joy-palette-divider)',
                                            bgcolor: active ? `${t.c}1A` : 'var(--joy-palette-background-level2)',
                                            cursor: submitting ? 'default' : 'pointer',
                                            display: 'flex', flexDirection: 'column',
                                            alignItems: 'center', gap: 0.4,
                                            transition: 'all .15s',
                                            opacity: submitting ? 0.6 : 1,
                                            '&:hover': !submitting ? {
                                                borderColor: t.c,
                                                bgcolor: active ? `${t.c}1A` : 'var(--joy-palette-background-level3)',
                                            } : {},
                                        }}
                                    >
                                        <Box sx={{ color: active ? t.c : 'var(--joy-palette-text-secondary)' }}>
                                            {t.icon}
                                        </Box>
                                        <Typography sx={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: active ? t.c : 'var(--joy-palette-text-secondary)',
                                        }}>
                                            {t.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* ── Кнопки ───────────────────────────────── */}
                    <Box sx={{ display: 'flex', gap: 1.2, pt: 0.5 }}>
                        <Button
                            variant="outlined" color="neutral"
                            onClick={onClose}
                            disabled={submitting}
                            sx={{ flex: 1, fontWeight: 700 }}
                        >
                            Отмена
                        </Button>
                        <Button
                            variant="solid" color="primary"
                            onClick={handleCreate}
                            loading={submitting}
                            startDecorator={!submitting && <AddRoundedIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                flex: 1, fontWeight: 700,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500), var(--joy-palette-primary-700))',
                                boxShadow: '0 8px 24px rgba(162,38,226,0.35)',
                            }}
                        >
                            Добавить чек
                        </Button>
                    </Box>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}